import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Send, BrainCircuit, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  fetchDiagnosticQuestions,
  createDiagnosticSession,
  upsertDiagnosticAnswer,
  submitDiagnosticSession,
  generateAIReport,
  fetchDiagnosticScores,
  fetchAIReport,
  fetchUserCompany,
  createCompany,
  fetchUserProfile,
} from "@/services/diagnosticService";
import DiagnosticProgress from "@/components/diagnostic/DiagnosticProgress";
import QuestionRenderer from "@/components/diagnostic/QuestionRenderer";
import DiagnosticResults from "@/components/diagnostic/DiagnosticResults";
import AuthModal from "@/components/diagnostic/AuthModal";
import ProfileCompletionModal from "@/components/diagnostic/ProfileCompletionModal";

const CATEGORY_ORDER = ["finance", "time", "digital", "strategy"];

const categoryLabels = {
  finance: "Finance",
  time: "Temps dirigeant",
  digital: "Digitalisation",
  strategy: "Stratégie",
};

export default function DiagnosticPage() {
  const { user, loading: authLoading } = useAuth();

  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [sessionId, setSessionId] = useState(null);
  const sessionIdRef = useRef(null); // Keep a ref for async callbacks
  const [phase, setPhase] = useState("questionnaire"); // questionnaire | analyzing | results | error
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [scores, setScores] = useState(null);
  const [report, setReport] = useState(null);
  const [userCompany, setUserCompany] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [savingAnswer, setSavingAnswer] = useState(false);
  
  // Track if we should auto-submit after profile completion
  const pendingSubmitRef = useRef(false);

  const availableCategories = [...new Set(questions.map(q => q.pillar))].sort((a, b) => {
    const ia = CATEGORY_ORDER.indexOf(a);
    const ib = CATEGORY_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });

  const questionsByCategory = availableCategories.reduce((acc, cat) => {
    acc[cat] = questions.filter((q) => q.pillar === cat);
    return acc;
  }, {});
  const currentCategory = availableCategories[currentCategoryIndex];
  const currentCategoryQuestions = questionsByCategory[currentCategory] || [];
  const currentQuestion = currentCategoryQuestions[currentQuestionIndex];
  const totalQuestions = questions.length;

  // Load questions on mount
  useEffect(() => {
    async function load() {
      try {
        const data = await fetchDiagnosticQuestions();
        setQuestions(data || []);
      } catch (err) {
        toast.error(err.message);
        setErrorMsg(err.message);
        setPhase("error");
      } finally {
        setLoadingQuestions(false);
      }
    }
    load();
  }, []);

  // Auto-close auth modal when user signs in (from another tab)
  useEffect(() => {
    if (user && showAuthModal) {
      // User just signed in - close modal and check profile
      setShowAuthModal(false);
      pendingSubmitRef.current = true;
      checkProfileAndProceed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Check if user profile is complete
  const checkProfileAndProceed = async () => {
    if (!user) return;
    
    try {
      const [profile, company] = await Promise.all([
        fetchUserProfile(user.id).catch(() => null),
        fetchUserCompany(user.id).catch(() => null),
      ]);
      
      setUserCompany(company);
      
      // Check if profile needs completion
      const needsPhone = !profile?.phone;
      const needsIndustry = !company?.industry;
      const needsSize = !company?.size_range;
      const needsCity = !company?.city;
      
      if (needsPhone || needsIndustry || needsSize || needsCity) {
        // Show profile completion modal
        setShowProfileModal(true);
      } else {
        // Profile is complete - proceed with submit
        if (pendingSubmitRef.current) {
          pendingSubmitRef.current = false;
          handleSubmit();
        }
      }
    } catch (err) {
      console.error("Error checking profile:", err);
      // Proceed anyway
      if (pendingSubmitRef.current) {
        pendingSubmitRef.current = false;
        handleSubmit();
      }
    }
  };

  // Handle profile modal completion
  const handleProfileComplete = () => {
    setShowProfileModal(false);
    if (pendingSubmitRef.current) {
      pendingSubmitRef.current = false;
      handleSubmit();
    }
  };

  // Handle profile modal skip
  const handleProfileSkip = () => {
    setShowProfileModal(false);
    // Mark as skipped in sessionStorage so it shows again next time
    sessionStorage.setItem("profileSkipped", "true");
    if (pendingSubmitRef.current) {
      pendingSubmitRef.current = false;
      handleSubmit();
    }
  };

  // Resolve the correct answer_label from question's answer_options
  function resolveAnswerLabel(question, value) {
    const opts = question.answer_options;
    const parsedOpts = typeof opts === "string" ? JSON.parse(opts) : opts;

    switch (question.question_type) {
      case "single_choice": {
        if (!parsedOpts) return String(value);
        const match = parsedOpts.find(o => (typeof o === "object" ? o.value : o) === value);
        return match ? (typeof match === "object" ? match.label : match) : String(value);
      }
      case "multi_choice": {
        if (!Array.isArray(value) || !parsedOpts) return null;
        return value.map(v => {
          const match = parsedOpts.find(o => (typeof o === "object" ? o.value : o) === v);
          return match ? (typeof match === "object" ? match.label : match) : v;
        }).join(", ");
      }
      case "scale_1_10":
        return String(value);
      case "free_text":
        return typeof value === "string" ? value : null;
      default:
        return typeof value === "string" ? value : null;
    }
  }

  // Resolve score_value from question's answer_options (for single_choice)
  function resolveScoreValue(question, value) {
    const opts = question.answer_options;
    const parsedOpts = typeof opts === "string" ? JSON.parse(opts) : opts;

    switch (question.question_type) {
      case "single_choice": {
        if (!parsedOpts) return null;
        const match = parsedOpts.find(o => (typeof o === "object" ? o.value : o) === value);
        return match?.score ?? null;
      }
      case "scale_1_10":
        return typeof value === "number" ? value : null;
      case "multi_choice": {
        if (!Array.isArray(value) || !parsedOpts) return null;
        const total = value.reduce((sum, v) => {
          const match = parsedOpts.find(o => (typeof o === "object" ? o.value : o) === v);
          return sum + (match?.score ?? 0);
        }, 0);
        return total || null;
      }
      default:
        return null;
    }
  }

  // Save answer to database
  const saveAnswer = useCallback(async (questionId, value) => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    const question = questions.find(q => q.id === questionId);
    if (!question) return;
    setSavingAnswer(true);
    try {
      const answerValue = typeof value === "object" ? JSON.stringify(value) : String(value);
      const answerLabel = resolveAnswerLabel(question, value);
      const scoreValue = resolveScoreValue(question, value);
      await upsertDiagnosticAnswer({
        diagnosticSessionId: sid,
        questionId,
        answerValue,
        answerLabel,
        scoreValue,
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingAnswer(false);
    }
  }, [questions]);

  const handleAnswerChange = (value) => {
    if (!currentQuestion) return;
    const qId = currentQuestion.id;
    setAnswers((prev) => ({ ...prev, [qId]: value }));
    if (sessionIdRef.current) {
      saveAnswer(qId, value);
    }
  };

  // Ensure session exists
  const ensureSession = useCallback(async () => {
    if (sessionIdRef.current) return sessionIdRef.current;
    if (!user) return null;

    try {
      let company = await fetchUserCompany(user.id);
      if (!company) {
        company = await createCompany({
          userId: user.id,
          companyName: user.user_metadata?.full_name || "Mon entreprise",
          industry: null,
        });
      }
      setUserCompany(company);
      const newSessionId = await createDiagnosticSession(company.id);
      setSessionId(newSessionId);
      sessionIdRef.current = newSessionId;
      return newSessionId;
    } catch (err) {
      toast.error(`Erreur de session: ${err.message}`);
      return null;
    }
  }, [user]);

  const goNext = () => {
    if (currentQuestionIndex < currentCategoryQuestions.length - 1) {
      setCurrentQuestionIndex((i) => i + 1);
    } else if (currentCategoryIndex < availableCategories.length - 1) {
      setCurrentCategoryIndex((i) => i + 1);
      setCurrentQuestionIndex(0);
    }
  };

  const goPrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((i) => i - 1);
    } else if (currentCategoryIndex > 0) {
      setCurrentCategoryIndex((i) => i - 1);
      const prevCat = availableCategories[currentCategoryIndex - 1];
      setCurrentQuestionIndex(questionsByCategory[prevCat].length - 1);
    }
  };

  const isFirstQuestion = currentCategoryIndex === 0 && currentQuestionIndex === 0;
  const isLastQuestion =
    currentCategoryIndex === availableCategories.length - 1 &&
    currentQuestionIndex === currentCategoryQuestions.length - 1;
  const currentAnswerValue = currentQuestion ? answers[currentQuestion.id] : undefined;
  const hasCurrentAnswer = currentAnswerValue !== undefined && currentAnswerValue !== "" &&
    !(Array.isArray(currentAnswerValue) && currentAnswerValue.length === 0);

  // Main submit handler
  const handleSubmit = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    setPhase("analyzing");
    try {
      const sid = await ensureSession();
      if (!sid) throw new Error("Impossible de créer la session.");

      // Save all unsaved answers
      for (const [qId, val] of Object.entries(answers)) {
        const question = questions.find(q => q.id === qId);
        const answerValue = typeof val === "object" ? JSON.stringify(val) : String(val);
        const answerLabel = question ? resolveAnswerLabel(question, val) : null;
        const scoreValue = question ? resolveScoreValue(question, val) : null;
        await upsertDiagnosticAnswer({ diagnosticSessionId: sid, questionId: qId, answerValue, answerLabel, scoreValue });
      }

      // Submit session
      await submitDiagnosticSession(sid);

      // Generate AI report
      await generateAIReport(sid);

      // Fetch results
      const [scoresData, reportData, companyData] = await Promise.all([
        fetchDiagnosticScores(sid),
        fetchAIReport(sid),
        fetchUserCompany(user.id),
      ]);

      // Verify we have valid data before showing results
      if (!scoresData || !reportData) {
        throw new Error("Les résultats n'ont pas pu être récupérés. Veuillez consulter Mon Espace.");
      }

      setScores(scoresData);
      setReport(reportData);
      setUserCompany(companyData);
      setPhase("results");

      // Send notification email (non-blocking)
      sendDiagnosticNotification({
        user,
        company: companyData,
        scores: scoresData,
        report: reportData,
      });
    } catch (err) {
      console.error("Submit error:", err);
      toast.error(err.message);
      setErrorMsg(err.message);
      setPhase("error");
    }
  };

  // Send email notification when diagnostic is complete (fire-and-forget)
  const sendDiagnosticNotification = async ({ user, company, scores, report }) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      await fetch(`${API_URL}/api/notify-diagnostic-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Client',
          client_email: user?.email,
          company_name: company?.company_name || 'Non renseigné',
          industry: company?.industry || null,
          city: company?.city || null,
          global_score: scores?.global_score || null,
          maturity_level: scores?.maturity_level || null,
          priority_1: scores?.priority_1 || null,
          priority_2: scores?.priority_2 || null,
          priority_3: scores?.priority_3 || null,
          recommended_services: report?.recommended_services || [],
        }),
      });
    } catch (err) {
      console.error('Failed to send diagnostic notification:', err);
    }
  };

  // Auth modal success handler (manual click)
  const handleAuthSuccess = async () => {
    setShowAuthModal(false);
    pendingSubmitRef.current = true;
    // Check profile completion
    await checkProfileAndProceed();
  };

  // Loading state
  if (loadingQuestions || authLoading) {
    return (
      <div data-testid="diagnostic-loading" className="min-h-screen flex items-center justify-center bg-[#0B1F3B] pt-20">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-[#59C3A6] mx-auto mb-4" />
          <p className="text-slate-400">Chargement du diagnostic...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (phase === "error") {
    return (
      <div data-testid="diagnostic-error" className="min-h-screen flex items-center justify-center bg-[#0B1F3B] pt-20 px-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-5">
            <AlertCircle size={32} className="text-red-400" />
          </div>
          <h2 className="font-heading text-2xl font-semibold text-white mb-3">Une erreur est survenue</h2>
          <p className="text-slate-400 text-sm mb-6">{errorMsg}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              data-testid="diagnostic-retry"
              onClick={() => { 
                setPhase("analyzing"); 
                setErrorMsg(""); 
                handleSubmit(); 
              }}
              className="bg-[#59C3A6] text-[#0B1F3B] hover:bg-[#4AB095] font-semibold px-6 py-2.5 rounded-full"
            >
              Réessayer
            </Button>
            <Link to="/mon-espace">
              <Button
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-white/5 font-semibold px-6 py-2.5 rounded-full"
              >
                Aller sur Mon Espace
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Analyzing state
  if (phase === "analyzing") {
    return (
      <div data-testid="diagnostic-analyzing" className="min-h-screen flex items-center justify-center bg-[#0B1F3B] pt-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            className="w-20 h-20 rounded-full border-2 border-[#59C3A6]/20 border-t-[#59C3A6] mx-auto mb-6"
          />
          <BrainCircuit size={32} className="text-[#59C3A6] mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-semibold text-white mb-2">Analyse en cours...</h2>
          <p className="text-slate-400 text-sm max-w-sm">
            Notre intelligence artificielle analyse vos réponses pour générer votre rapport personnalisé.
          </p>
        </motion.div>
      </div>
    );
  }

  // Results state
  if (phase === "results") {
    // Fallback if report is null (edge case)
    if (!report || !scores) {
      return (
        <div className="min-h-screen bg-[#0B1F3B] pt-28 pb-20 px-6 md:px-12 lg:px-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-16 h-16 rounded-full bg-[#59C3A6]/20 flex items-center justify-center mx-auto mb-5">
              <AlertCircle size={32} className="text-[#59C3A6]" />
            </div>
            <h2 className="font-heading text-2xl font-semibold text-white mb-3">
              Votre rapport a été généré
            </h2>
            <p className="text-slate-400 text-base mb-6">
              Rendez-vous dans Mon Espace pour consulter vos résultats.
            </p>
            <Link to="/mon-espace">
              <Button className="bg-[#59C3A6] text-[#0B1F3B] hover:bg-[#4AB095] font-semibold px-8 py-3 rounded-full">
                Accéder à Mon Espace
              </Button>
            </Link>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-[#0B1F3B] pt-28 pb-20 px-6 md:px-12 lg:px-16">
        <div className="max-w-4xl mx-auto">
          <DiagnosticResults scores={scores} report={report} sessionId={sessionIdRef.current} />
        </div>
      </div>
    );
  }

  // Questionnaire state (default)
  return (
    <div data-testid="diagnostic-page" className="min-h-screen bg-[#0B1F3B] pt-28 pb-20 px-6 md:px-12 lg:px-16">
      <div className="max-w-3xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
            Diagnostic de votre activité
          </h1>
          <p className="text-slate-400 text-base">
            Répondez aux questions pour recevoir une analyse personnalisée de votre entreprise.
          </p>
        </motion.div>

        <DiagnosticProgress
          categories={availableCategories}
          currentIndex={currentCategoryIndex}
          answers={answers}
          totalQuestions={totalQuestions}
        />

        <div className="mb-6">
          <span className="text-[#59C3A6] text-sm font-semibold uppercase tracking-wider">
            {categoryLabels[currentCategory] || currentCategory?.replace(/_/g, ' ')}
          </span>
          <span className="text-slate-500 text-sm ml-3">
            Question {currentQuestionIndex + 1} / {currentCategoryQuestions.length}
          </span>
        </div>

        {currentQuestion && (
          <div className="p-6 md:p-8 rounded-2xl bg-[#112A4F]/30 border border-white/5 mb-6">
            <QuestionRenderer
              key={currentQuestion.id}
              question={currentQuestion}
              value={currentAnswerValue}
              onChange={handleAnswerChange}
            />
          </div>
        )}

        <div className="flex items-center justify-between gap-4">
          <Button
            data-testid="diagnostic-prev"
            onClick={goPrev}
            disabled={isFirstQuestion}
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-white/5 hover:text-white px-5 py-2.5 rounded-full bg-transparent disabled:opacity-30"
          >
            <ArrowLeft size={16} className="mr-2" /> Précédent
          </Button>

          {savingAnswer && (
            <span className="text-slate-500 text-xs flex items-center gap-1">
              <Loader2 size={12} className="animate-spin" /> Sauvegarde...
            </span>
          )}

          {isLastQuestion ? (
            <Button
              data-testid="diagnostic-submit"
              onClick={handleSubmit}
              disabled={!hasCurrentAnswer}
              className="bg-[#59C3A6] text-[#0B1F3B] hover:bg-[#4AB095] font-semibold px-6 py-2.5 rounded-full transition-all duration-300 hover:scale-105 shadow-[0_0_20px_rgba(89,195,166,0.25)] disabled:opacity-50 disabled:hover:scale-100"
            >
              <Send size={16} className="mr-2" /> Soumettre mon diagnostic
            </Button>
          ) : (
            <Button
              data-testid="diagnostic-next"
              onClick={goNext}
              disabled={!hasCurrentAnswer}
              className="bg-[#59C3A6] text-[#0B1F3B] hover:bg-[#4AB095] font-semibold px-6 py-2.5 rounded-full transition-all duration-300 disabled:opacity-50"
            >
              Suivant <ArrowRight size={16} className="ml-2" />
            </Button>
          )}
        </div>
      </div>

      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
        />
      )}

      {/* Profile Completion Modal */}
      {showProfileModal && (
        <ProfileCompletionModal
          user={user}
          company={userCompany}
          onComplete={handleProfileComplete}
          onSkip={handleProfileSkip}
        />
      )}
    </div>
  );
}
